// Columna izquierda del explorer (`.col-filters` del mockup): los SEIS grupos, en su orden —
// Búsqueda · Estado · Tipo · Tienda · Solicitado por · Detectado.
import { Search } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { Input, Select } from '@/kit';
import { cn } from '@/lib/cn';
import { mxDayOffset } from '@/lib/dateHelpers';
import type { ExceptionStatus, ExceptionType } from '@/api/services/retail-wizard/backoffice';
import {
    countActiveFilters,
    EMPTY_EXCEPCIONES_FILTERS,
    EXCEPTION_STATUS_VALUES,
    EXCEPTION_TYPE_VALUES,
    STATUS_CHIP_CLASS,
    STATUS_LABEL,
    STATUS_SHORT,
    toggleInList,
    TYPE_LONG_LABEL,
    type ExcepcionesFilters,
} from '../lib/excepcionesFormat';

/** Valor centinela de los selects: Radix no admite `value=""` en un item. */
const ALL = 'ALL';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD = 'h-[26px] text-[11.5px]';
const CHIP =
    'cursor-pointer whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[9.5px] font-semibold transition-colors';
const SHORTCUT =
    'cursor-pointer rounded border border-border px-1.5 py-0.5 text-[10px] transition-colors hover:bg-accent';

/** Los cuatro atajos del mockup: 7d · 30d · Trim · Todo. */
const DATE_SHORTCUTS: { label: string; days: number }[] = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: 'Trim', days: 90 },
];

export interface PickerOption {
    value: string;
    label: string;
}

export function ExcepcionesFiltersPanel({
    filters,
    onChange,
    stores,
    requesters,
    collapsed,
    onToggleCollapsed,
}: {
    filters: ExcepcionesFilters;
    onChange: (next: ExcepcionesFilters) => void;
    /**
     * Las tiendas OFRECIDAS en el combo. Salen de las excepciones que hay en la bandeja, no del
     * padrón entero: un filtro que ofrece una tienda sin ni un caso deja la tabla vacía sin
     * explicar por qué. El alta sí usa el padrón completo — ahí la tienda todavía no tiene casos.
     */
    stores: PickerOption[];
    /** Mismo criterio: quienes efectivamente levantaron alguna de las excepciones cargadas. */
    requesters: PickerOption[];
    collapsed: boolean;
    onToggleCollapsed: () => void;
}) {
    const patch = (partial: Partial<ExcepcionesFilters>) => {
        onChange({ ...filters, ...partial });
    };
    const activeCount = countActiveFilters(filters);

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeCount}
            onReset={() => {
                onChange(EMPTY_EXCEPCIONES_FILTERS);
            }}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                    />
                    <Input
                        type="search"
                        value={filters.query}
                        onChange={(e) => {
                            patch({ query: e.target.value });
                        }}
                        placeholder="Folio, venta, motivo"
                        aria-label="Buscar excepción por folio, venta o motivo"
                        className={cn(FIELD, 'pl-6')}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Estado</div>
                <div className="flex flex-wrap gap-[3px]">
                    {EXCEPTION_STATUS_VALUES.map((status) => {
                        const on = filters.statuses.includes(status);
                        return (
                            <button
                                key={status}
                                type="button"
                                title={STATUS_LABEL[status]}
                                aria-pressed={on}
                                onClick={() => {
                                    patch({
                                        statuses: toggleInList<ExceptionStatus>(
                                            filters.statuses,
                                            status,
                                        ),
                                    });
                                }}
                                className={cn(
                                    CHIP,
                                    on
                                        ? STATUS_CHIP_CLASS[status].on
                                        : cn(STATUS_CHIP_CLASS[status].off, 'hover:bg-accent'),
                                )}
                            >
                                {STATUS_SHORT[status]}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Tipo</div>
                <Select
                    id="flt-exc-type"
                    value={filters.type === '' ? ALL : filters.type}
                    onChange={(value) => {
                        patch({ type: value === ALL ? '' : (value as ExceptionType) });
                    }}
                    options={[
                        { value: ALL, label: 'Todos' },
                        // ✂️ `autorizacion_tope` es el sexto del mockup y el spec lo recorta:
                        // depende de conciliación. No se ofrece un filtro que no puede aplicar.
                        ...EXCEPTION_TYPE_VALUES.map((type) => ({
                            value: type,
                            label: TYPE_LONG_LABEL[type],
                        })),
                    ]}
                    className={FIELD}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Tienda</div>
                <Select
                    id="flt-exc-store"
                    value={filters.storeId === '' ? ALL : filters.storeId}
                    onChange={(value) => {
                        patch({ storeId: value === ALL ? '' : value });
                    }}
                    options={[{ value: ALL, label: 'Todas las tiendas' }, ...stores]}
                    disabled={stores.length === 0}
                    className={FIELD}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Solicitado por</div>
                <Select
                    id="flt-exc-requester"
                    value={filters.requestedBy === '' ? ALL : filters.requestedBy}
                    onChange={(value) => {
                        patch({ requestedBy: value === ALL ? '' : value });
                    }}
                    options={[{ value: ALL, label: 'Cualquiera' }, ...requesters]}
                    disabled={requesters.length === 0}
                    className={FIELD}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Solicitada</div>
                <Input
                    type="date"
                    value={filters.from}
                    onChange={(e) => {
                        patch({ from: e.target.value });
                    }}
                    aria-label="Solicitada desde"
                    className={cn(FIELD, 'mb-1 w-full')}
                />
                <Input
                    type="date"
                    value={filters.to}
                    onChange={(e) => {
                        patch({ to: e.target.value });
                    }}
                    aria-label="Solicitada hasta"
                    className={cn(FIELD, 'mb-1 w-full')}
                />
                <div className="grid grid-cols-4 gap-[3px]">
                    {DATE_SHORTCUTS.map((shortcut) => (
                        <button
                            key={shortcut.label}
                            type="button"
                            onClick={() => {
                                patch({
                                    from: mxDayOffset(shortcut.days),
                                    to: mxDayOffset(0),
                                });
                            }}
                            className={SHORTCUT}
                        >
                            {shortcut.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            patch({ from: '', to: '' });
                        }}
                        className={SHORTCUT}
                    >
                        Todo
                    </button>
                </div>
            </div>
        </FiltersColumn>
    );
}
